import { ContactState, GroupStatusEvent, useIM } from '@wui/im'
import { defineStore } from 'pinia'
import { shallowRef, triggerRef, watch, ref } from 'vue'

import { useCallSessionRoomStore } from '@/entities/call-session'
import { useWebRTC } from '@/entities/webrtc'

import { emitter } from '@/shared/services/emitter'
import { EmitterEvents } from '@/shared/types/emitter-events'
import { fromPServedUser } from '@/shared/utils/uri'

export enum MemberStatus {
  Inactive = 'Inactive',
  Dialing = 'Dialing',
  Active = 'Active',
  OnHold = 'OnHold',
  Awaiting = 'Awaiting',
  RequestMedia = 'RequestMedia'
}

export interface CallStatus {
  [k: string]: MemberStatus
}

export interface MemberStatuses {
  [k: string]: CallStatus
}

export interface RoomStatus {
  active: boolean
}

interface RoomParticipantsStatuses {
  [k: string]: MemberStatuses
}

const memberStatusByNumber: {
  [key: number]: MemberStatus
} = {
  0: MemberStatus.Inactive,
  1: MemberStatus.Dialing,
  2: MemberStatus.Active,
  3: MemberStatus.OnHold,
}

const toMemberStatuses = (contacts: ContactState[] | undefined): MemberStatuses => {
  const result = {} as MemberStatuses
  if (!contacts?.length) {
    return result
  }

  contacts.forEach(contact => {
    contact.calls?.forEach(call => {
      const number = fromPServedUser(contact.id)
      if (!result[number]) {
        result[number] = {}
      }
      result[number][call.id] = memberStatusByNumber[call.state] ?? MemberStatus.Inactive
    })
  })

  return result
}

const SEPARATOR = '_'

export const useIntercomRoomParticipantsStatusesStore = defineStore('intercom-room-participants-statuses', () => {
  const { onConferenceUpdated } = useIM()

  const roomsStatuses = shallowRef<{ [guid: string]: RoomStatus }>({})
  const roomsParticipantsStatuses = shallowRef<RoomParticipantsStatuses>({})
  const activeRoomsActiveParticipantsCount = ref<string[]>([])

  const updateRoomParticipantStatuses = (
    pServedUser: string,
    contactStates: ContactState[],
  ): void => {
    const statuses: MemberStatuses = toMemberStatuses(contactStates)

    const pServed = fromPServedUser(pServedUser)
    roomsParticipantsStatuses.value[pServed] = statuses

    const activeParticipantsCount = Object.values(statuses).filter(o => Object.values(o).find(i => i === MemberStatus.Active)).length
    const active = !!activeParticipantsCount
    roomsStatuses.value[pServed] = {
      active,
    }

    activeRoomsActiveParticipantsCount.value = activeRoomsActiveParticipantsCount.value.filter(i => !i.startsWith(pServedUser))
    if (active) {
      activeRoomsActiveParticipantsCount.value.push(`${pServedUser}${SEPARATOR}${activeParticipantsCount}`)
    }

    triggerRef(roomsParticipantsStatuses)
    triggerRef(roomsStatuses)
  }

  const getRoomStatus = (pServedUser: string): RoomStatus => {
    return roomsStatuses.value[fromPServedUser(pServedUser)] ?? { active: false }
  }

  const getParticipantStatus = (pServedRoom: string, contactId: string, callId: string): MemberStatus => {
    const roomStatuses = roomsParticipantsStatuses.value[fromPServedUser(pServedRoom)]
    if (!roomStatuses || !roomStatuses[contactId]) {
      return MemberStatus.Inactive
    }

    return roomsParticipantsStatuses.value[fromPServedUser(pServedRoom)]?.[contactId]?.[callId] || MemberStatus.Inactive
  }

  const getParticipantCallsStatuses = (pServedRoom: string, contactId: string): CallStatus => {
    return roomsParticipantsStatuses.value[fromPServedUser(pServedRoom)]?.[contactId] || {}
  }

  const getIsParticipantIsActive = (pServedRoom: string, contactId: string): boolean => {
    const roomStatuses = roomsParticipantsStatuses.value[fromPServedUser(pServedRoom)]
    if (!roomStatuses) {
      return false
    }

    return !!Object.values(roomStatuses[contactId] ?? {}).find(s => s !== MemberStatus.Inactive)
  }

  const getIsRoomActive = (pServedUser: string): boolean => {
    return roomsStatuses.value[fromPServedUser(pServedUser)]?.active ?? false
  }

  onConferenceUpdated((event: GroupStatusEvent) => {
    const { pServed, extendedParams: { contacts }} = event
    if (contacts) {
      updateRoomParticipantStatuses(pServed, contacts)
    }
  })

  const { sessions } = useWebRTC()
  const { getRoom: getSessionRoom } = useCallSessionRoomStore()
  watch(activeRoomsActiveParticipantsCount, (n, o) => {
    n.forEach(i => {
      const [roomPServed, newCount] = i.split(SEPARATOR)

      const old = o.find(i => i?.startsWith(roomPServed ?? ''))
      const callId = Array.from(sessions.value.values()).find(s => getSessionRoom(s.sessionId)?.pServedUser === roomPServed)?.callId.value
      if (old) {
        const [, oldCount] = old.split(SEPARATOR)
        if (newCount !== oldCount && newCount !== undefined) {
          emitter.emit(EmitterEvents.CHANGE_ACTIVE_PARTICIPANTS_COUNT_IN_ACTIVE_ROOM, { roomPServed, count: +newCount, callId })
        }
      } else {
        emitter.emit(EmitterEvents.CHANGE_ACTIVE_PARTICIPANTS_COUNT_IN_ACTIVE_ROOM, { roomPServed, count: newCount !== undefined ? +newCount : 0, callId })
      }
    })
  }, {
    deep: true,
  })

  return {
    getRoomStatus,
    getParticipantStatus,
    getIsRoomActive,
    getIsParticipantIsActive,
    getParticipantCallsStatuses,
  }
})
